/**
 * Session User Reads
 *
 * Keeps the store's user in step with the server's, on the website and in the
 * apps. A change made elsewhere (another browser, a phone, a link opened from
 * an email) shows once the user is read again. The store's `fetchUser` is the
 * one path that reads it; this says when.
 *
 * - `readSessionUser` reads now. The website does, once per page load.
 * - `rereadSessionUser` reads again, at most every 30 seconds and only while
 *   signed in: on coming back to the tab, and in an app at launch and on
 *   coming back to it.
 *
 * An arriving account notice reads it at once too: notification.store asks
 * through store-sync, where this module registers `readSessionUser`.
 */

import { authStore } from './auth.store';
import { registerSessionUserReader } from './store-sync';

/** The least time between reads again. */
export const REREAD_INTERVAL_MS = 30_000;

/** When the last read began, as `performance.now()` reads: never, at first. */
let lastReadAt = Number.NEGATIVE_INFINITY;

/** Ask the store to read the server's user, now. */
export async function readSessionUser(): Promise<void> {
  lastReadAt = performance.now();
  await authStore.getState().fetchUser();
}

/** Signed in, read the user again, unless it was read in the last 30 seconds. */
export async function rereadSessionUser(): Promise<void> {
  if (!authStore.getState().isAuthenticated) return;
  if (performance.now() - lastReadAt < REREAD_INTERVAL_MS) return;
  await readSessionUser();
}

/** Forgets when it last read, as a new page load or launch would. For tests. */
export function resetSessionUserReads(): void {
  lastReadAt = Number.NEGATIVE_INFINITY;
}

registerSessionUserReader(readSessionUser);
